import React, { useState, useEffect, useCallback, useRef } from "react";
import { Form, Flex, Input, Row, Col, message } from 'antd'
import { QuestionCircleOutlined, EnvironmentOutlined } from '@ant-design/icons'
import { useJsApiLoader, Autocomplete, GoogleMap, MarkerF } from '@react-google-maps/api';
import { getActiveSetValue, getConditionalRevertValue, shouldRevertConditionalValue } from "../../../utils/formsHelper";
import { useTranslation } from "react-i18next";

// Google Maps API configuration
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const libraries = ['places'];

const mapContainerStyle = {
    width: '100%',
    height: '300px',
    borderRadius: '6px',
    marginTop: '12px'
};

const defaultCenter = {
    lat: 28.584669,
    lng: 77.352005
};

const AddressInput = (props) => {
    const { id, label, placeholder, tooltip, helpText, mandatory, form, readOnly, mapEnabled = false, setValueData } = props
    const { t } = useTranslation();
    const rules = mandatory ? [{ required: true, whitespace: true }] : undefined;
    const disabled = readOnly ? true : false
    const help = tooltip ? { title: helpText, icon: <QuestionCircleOutlined /> } : undefined;

    // State for address components
    const [addressComponents, setAddressComponents] = useState({
        street: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        fullAddress: ''
    });

    const [autocomplete, setAutocomplete] = useState(null);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [markerPosition, setMarkerPosition] = useState(null);
    let activeSetValue = getActiveSetValue(setValueData, true)

    // The address the condition last wrote, so it can be taken back once the
    // condition stops passing without discarding one the user entered since.
    const appliedFormValueRef = useRef(undefined)

    useEffect(() => {
        if (!mapEnabled) {
            if (activeSetValue?.value) {
                form?.setFieldValue(id, activeSetValue.value)
                appliedFormValueRef.current = activeSetValue.value
            } else if (shouldRevertConditionalValue(activeSetValue, { disabled, currentValue: form?.getFieldValue(id), appliedValue: appliedFormValueRef.current })) {
                form?.setFieldValue(id, getConditionalRevertValue(id, props.defaultFormValues))
                appliedFormValueRef.current = undefined
            } else {
                appliedFormValueRef.current = undefined
            }
        }
    }, [activeSetValue, form, id, disabled, mapEnabled])

    // Load Google Maps API using the React hook
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: libraries
    });

    const onLoad = useCallback((autocompleteInstance) => {
        setAutocomplete(autocompleteInstance);
    }, []);

    const onUnmount = useCallback(() => {
        setAutocomplete(null);
    }, []);

    const onPlaceChanged = useCallback(() => {
        if (autocomplete !== null) {
            const place = autocomplete.getPlace();
            handlePlaceSelect(place);
        }
    }, [autocomplete]);

    const handlePlaceSelect = (place) => {
        if (!place.geometry) {
            message.warning(t('No details available for this location'));
            return;
        }

        const components = {
            street: '',
            city: '',
            state: '',
            country: '',
            postalCode: '',
            fullAddress: place.formatted_address || ''
        };

        // Parse address components
        if (place.address_components) {
            place.address_components.forEach((component) => {
                const types = component.types;

                if (types.includes('street_number')) {
                    components.street = component.long_name + ' ';
                }
                if (types.includes('route')) {
                    components.street += component.long_name;
                }
                if (types.includes('locality')) {
                    components.city = component.long_name;
                }
                if (types.includes('administrative_area_level_1')) {
                    components.state = component.long_name;
                }
                if (types.includes('country')) {
                    components.country = component.long_name;
                }
                if (types.includes('postal_code')) {
                    components.postalCode = component.long_name;
                }
            });
        }

        setAddressComponents(components);

        // Update map position if location is available
        if (place.geometry && place.geometry.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            const newPosition = { lat, lng };
            setMapCenter(newPosition);
            setMarkerPosition(newPosition);
        }
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setAddressComponents(prev => ({
            ...prev,
            fullAddress: newValue
        }));
    };

    const onMapClick = useCallback((event) => {
        if (!disabled) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            const newPosition = { lat, lng };
            setMarkerPosition(newPosition);
            setMapCenter(newPosition);

            // Reverse geocoding to get address from coordinates
            if (window.google && window.google.maps) {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: newPosition }, (results, status) => {
                    if (status === 'OK' && results[0]) {
                        const place = results[0];
                        setAddressComponents(prev => ({
                            ...prev,
                            fullAddress: place.formatted_address
                        }));
                    }
                });
            }
        }
    }, [disabled]);

    // Simple address input without map
    if (!mapEnabled) {
        return (
            <div className="form-element">
                <Flex vertical>
                    <Form.Item name={id} label={t("Address")} rules={rules} tooltip={t("Enter help text")}>
                        <Input.TextArea
                            placeholder={t("Enter your full address")}
                            disabled={disabled}
                            rows={3}
                            showCount
                            maxLength={500}
                            style={{ resize: 'vertical' }}
                        />
                    </Form.Item>
                </Flex>
            </div>
        );
    }

    // Handle loading states
    if (loadError) {
        return (
            <div className="form-element">
                <Flex vertical>
                    <Form.Item name={id} label={t("Address")} rules={rules} tooltip={t("Enter help text")}>
                        <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px', backgroundColor: '#fff2f0' }}>
                            <Input
                                placeholder={t("Enter your full address")}
                                disabled={disabled}
                                prefix={<EnvironmentOutlined />}
                                value={addressComponents.fullAddress}
                                onChange={handleInputChange}
                                size="large"
                            />
                            <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                                ⚠️ {t("Could not load Google Maps. Please enter address manually.")}
                            </div>
                        </div>
                    </Form.Item>
                </Flex>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="form-element">
                <Flex vertical>
                    <Form.Item name={id} label={label || "Address"} rules={rules} tooltip={help}>
                        <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px', backgroundColor: '#fafafa' }}>
                            <Input
                                placeholder={t('Loading address autocomplete...')}
                                disabled={true}
                                prefix={<EnvironmentOutlined />}
                                size="large"
                            />
                            <div style={{ color: '#1890ff', fontSize: '12px', marginTop: '4px' }}>
                                🔄 Loading Google Maps API...
                            </div>
                        </div>
                    </Form.Item>
                </Flex>
            </div>
        );
    }

    // Address input with Google Places autocomplete and map
    return (
        <div className="form-element">
            <Flex vertical>
                <Form.Item name={id} label={label || "Address"} rules={rules} tooltip={help}>
                    <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px', backgroundColor: '#fafafa' }}>
                        {/* Main address input with autocomplete */}
                        <div style={{ marginBottom: '12px' }}>
                            <Autocomplete
                                onLoad={onLoad}
                                onPlaceChanged={onPlaceChanged}
                                onUnmount={onUnmount}
                                options={{
                                    types: ['address'],
                                    componentRestrictions: {}, // You can restrict to specific countries here
                                    fields: ['address_components', 'formatted_address', 'geometry']
                                }}
                            >
                                <Input
                                    placeholder={placeholder || "Start typing an address..."}
                                    disabled={disabled}
                                    prefix={<EnvironmentOutlined />}
                                    value={addressComponents.fullAddress}
                                    onChange={handleInputChange}
                                    size="large"
                                    autoComplete="off"
                                />
                            </Autocomplete>

                            {/* Status message */}
                            <div style={{ color: '#52c41a', fontSize: '12px', marginTop: '4px' }}>
                                ✓ Address autocomplete ready - start typing to see suggestions
                            </div>
                        </div>

                        {/* Google Map */}
                        <div style={{ marginBottom: '12px' }}>
                            <GoogleMap
                                mapContainerStyle={mapContainerStyle}
                                center={mapCenter}
                                zoom={15}
                                onClick={onMapClick}
                                options={{
                                    zoomControl: true,
                                    streetViewControl: false,
                                    fullscreenControl: false,
                                    mapTypeControl: false
                                }}
                            >
                                {markerPosition && (
                                    <MarkerF position={markerPosition} />
                                )}
                            </GoogleMap>
                            <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                📍 {t("Click on the map to set a precise location")}
                            </div>
                        </div>

                        {/* Address component breakdown */}
                        {/* {JSON.stringify(addressComponents.fullAddress)} */}
                        {/* {addressComponents.fullAddress && (
                            <Row gutter={[8, 8]}>
                                <Col span={24}>
                                    <Input
                                        placeholder="Street Address"
                                        value={addressComponents.street}
                                        onChange={(e) => setAddressComponents(prev => ({
                                            ...prev,
                                            street: e.target.value
                                        }))}
                                        size="small"
                                    />
                                </Col>
                                <Col span={12}>
                                    <Input
                                        placeholder="City"
                                        value={addressComponents.city}
                                        onChange={(e) => setAddressComponents(prev => ({
                                            ...prev,
                                            city: e.target.value
                                        }))}
                                        size="small"
                                    />
                                </Col>
                                <Col span={12}>
                                    <Input
                                        placeholder="State/Province"
                                        value={addressComponents.state}
                                        onChange={(e) => setAddressComponents(prev => ({
                                            ...prev,
                                            state: e.target.value
                                        }))}
                                        size="small"
                                    />
                                </Col>
                                <Col span={12}>
                                    <Input
                                        placeholder="Country"
                                        value={addressComponents.country}
                                        onChange={(e) => setAddressComponents(prev => ({
                                            ...prev,
                                            country: e.target.value
                                        }))}
                                        size="small"
                                    />
                                </Col>
                                <Col span={12}>
                                    <Input
                                        placeholder="Postal Code"
                                        value={addressComponents.postalCode}
                                        onChange={(e) => setAddressComponents(prev => ({
                                            ...prev,
                                            postalCode: e.target.value
                                        }))}
                                        size="small"
                                    />
                                </Col>
                            </Row>
                        )} */}
                    </div>
                </Form.Item>
            </Flex>
        </div>
    );
};

export default AddressInput